import React, { useState, useRef, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import SignatureCanvas from "react-signature-canvas";
import { PDFDocument, rgb } from "pdf-lib";
import { UserContext } from "../../context/UserContext";

// Use local worker file
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

function ContractSigning({ handleRestart }) {
  const navigate = useNavigate();
  const location = useLocation();
  const contractData = location.state?.contractData;
  const { logout } = useContext(UserContext);

  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [signatures, setSignatures] = useState({});
  const [initials, setInitials] = useState({});
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [currentSignatureField, setCurrentSignatureField] = useState(null);
  const [isInitialMode, setIsInitialMode] = useState(false);
  const [pageScale, setPageScale] = useState(1.0);
  const [pdfDimensions, setPdfDimensions] = useState({ width: 0, height: 0 });
  const [signatureMode, setSignatureMode] = useState("draw"); // 'draw' or 'type'
  const [typedSignature, setTypedSignature] = useState("");
  const [selectedFont, setSelectedFont] = useState("Brush Script MT");
  const [dates, setDates] = useState({});
  const [showDateModal, setShowDateModal] = useState(false);
  const [currentDateField, setCurrentDateField] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");

  const signatureCanvasRef = useRef();

  useEffect(() => {
    if (!contractData) {
      navigate("/", { replace: true });
    }
  }, [contractData, navigate]);

  if (!contractData) {
    return null;
  }

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const onPageLoadSuccess = (page) => {
    const viewport = page.getViewport({ scale: 1.0 });
    setPdfDimensions({ width: viewport.width, height: viewport.height });
  };

  const getUniqueSignatureTypes = () => {
    const types = new Set();
    contractData.fields.forEach((field) => {
      const baseType = field.type.replace(/-signature|-initial|-date$/, "");
      types.add(baseType);
    });
    return Array.from(types);
  };

  const openSignatureModal = (field) => {
    setCurrentSignatureField(field);
    setIsInitialMode(field.type.includes("initial"));
    setSignatureMode("draw");
    setTypedSignature("");
    setShowSignatureModal(true);
  };

  const openDateModal = (field) => {
    setCurrentDateField(field);
    const fieldKey = `${field.type}-${field.x}-${field.y}`;
    const existingDate = dates[fieldKey];

    if (existingDate) {
      setSelectedDate(existingDate);
    } else {
      // Default to today's date
      const today = new Date();
      const formattedDate = today.toISOString().split("T")[0];
      setSelectedDate(formattedDate);
    }

    setShowDateModal(true);
  };

  const saveDate = () => {
    if (!currentDateField || !selectedDate) return;

    const fieldKey = `${currentDateField.type}-${currentDateField.x}-${currentDateField.y}`;
    setDates((prev) => ({
      ...prev,
      [fieldKey]: selectedDate,
    }));

    clearDateModal();
  };

  const clearDateModal = () => {
    setShowDateModal(false);
    setCurrentDateField(null);
    setSelectedDate("");
  };

  const generateTypedSignature = () => {
    if (!typedSignature.trim()) return null;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    // Set canvas size
    canvas.width = 400;
    canvas.height = isInitialMode ? 80 : 120;

    // Set background
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Set text style
    ctx.fillStyle = "black";
    ctx.font = `${isInitialMode ? 36 : 48}px "${selectedFont}", cursive`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    // Draw text
    ctx.fillText(typedSignature, canvas.width / 2, canvas.height / 2);

    return canvas.toDataURL();
  };

  const saveSignature = () => {
    if (!currentSignatureField) return;

    let signatureDataURL;

    if (signatureMode === "draw") {
      if (!signatureCanvasRef.current) return;
      signatureDataURL = signatureCanvasRef.current.toDataURL();
    } else {
      signatureDataURL = generateTypedSignature();
      if (!signatureDataURL) return;
    }

    const fieldKey = `${currentSignatureField.type}-${currentSignatureField.x}-${currentSignatureField.y}`;

    if (isInitialMode) {
      setInitials((prev) => ({
        ...prev,
        [fieldKey]: signatureDataURL,
      }));
    } else {
      setSignatures((prev) => ({
        ...prev,
        [fieldKey]: signatureDataURL,
      }));
    }

    clearSignatureModal();
  };

  const clearSignatureModal = () => {
    setShowSignatureModal(false);
    setCurrentSignatureField(null);
    setIsInitialMode(false);
    if (signatureCanvasRef.current) {
      signatureCanvasRef.current.clear();
    }
  };

  const getFieldsForPage = (pageNum) => {
    return contractData.fields.filter((field) => field.page.includes(pageNum));
  };

  const calculateFieldPosition = (field) => {
    // Convert PDF coordinates to screen coordinates
    const scaleX = pageScale;
    const scaleY = pageScale;

    return {
      left: field.x * scaleX,
      top: (pdfDimensions.height - field.y - field.height) * scaleY,
      width: field.width * scaleX,
      height: field.height * scaleY,
    };
  };

  const getFieldSignature = (field) => {
    const fieldKey = `${field.type}-${field.x}-${field.y}`;
    return signatures[fieldKey] || initials[fieldKey];
  };

  const getFieldDate = (field) => {
    const fieldKey = `${field.type}-${field.x}-${field.y}`;
    return dates[fieldKey];
  };

  const hasFieldContent = (field) => {
    if (field.type.includes("date")) {
      return getFieldDate(field);
    }
    return getFieldSignature(field);
  };

  const downloadSignedPDF = async () => {
    try {
      // Convert base64 to array buffer
      const pdfBytes = Uint8Array.from(atob(contractData.pdf), (c) =>
        c.charCodeAt(0)
      );
      const pdfDoc = await PDFDocument.load(pdfBytes);

      // Add signatures and dates to PDF
      for (const field of contractData.fields) {
        const fieldKey = `${field.type}-${field.x}-${field.y}`;
        const signatureData = signatures[fieldKey] || initials[fieldKey];
        const dateData = dates[fieldKey];

        if (signatureData && !field.type.includes("date")) {
          // Convert signature image to embed in PDF
          const signatureImage = await pdfDoc.embedPng(signatureData);

          // Get signature dimensions to maintain aspect ratio
          const signatureDims = signatureImage.scale(1);
          const aspectRatio = signatureDims.width / signatureDims.height;

          // Calculate signature size - fill height and maintain aspect ratio
          const targetHeight = field.height;
          const targetWidth = Math.min(targetHeight * aspectRatio, field.width);

          // Place signature on ALL pages where this field appears
          for (const pageNum of field.page) {
            const page = pdfDoc.getPages()[pageNum - 1]; // field.page is 1-indexed

            // Draw signature on PDF (left-aligned within the field)
            page.drawImage(signatureImage, {
              x: field.x,
              y: field.y,
              width: targetWidth,
              height: targetHeight,
            });
          }
        }

        // Add selected date for date fields
        if (field.type.includes("date")) {
          const dateText = dateData
            ? (() => {
                const [year, month, day] = dateData.split('-');
                return new Date(year, month - 1, day).toLocaleDateString();
              })()
            : new Date().toLocaleDateString();

          // Place date on ALL pages where this field appears
          for (const pageNum of field.page) {
            const page = pdfDoc.getPages()[pageNum - 1]; // field.page is 1-indexed

            page.drawText(dateText, {
              x: field.x + 5,
              y: field.y + 5,
              size: 10,
              color: rgb(0, 0, 0),
            });
          }
        }
      }

      const signedPdfBytes = await pdfDoc.save();

      // Download the signed PDF
      const blob = new Blob([signedPdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `signed-contract-${contractData.agreement_number}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error creating signed PDF:", error);
      alert("Error creating signed PDF. Please try again.");
    }
  };

  const isAllFieldsSigned = () => {
    return contractData.fields.every((field) => {
      const fieldKey = `${field.type}-${field.x}-${field.y}`;
      if (field.type.includes("date")) {
        return dates[fieldKey]; // Check if date is set
      }
      return signatures[fieldKey] || initials[fieldKey];
    });
  };

  const getNextUnsignedField = () => {
    return contractData.fields.find((field) => {
      const fieldKey = `${field.type}-${field.x}-${field.y}`;
      if (field.type.includes("date")) {
        return !dates[fieldKey];
      }
      return !signatures[fieldKey] && !initials[fieldKey];
    });
  };

  const navigateToNextSignature = () => {
    const nextField = getNextUnsignedField();
    if (nextField) {
      // Navigate to the first page where this field appears
      setCurrentPage(nextField.page[0]);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="contract-signing-page">
      <div className="container">
        <div className="row pt-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h2>Sign Contract {contractData.agreement_number}</h2>
              <div>
                <button
                  className="btn btn-secondary me-2"
                  onClick={() => {
                    handleRestart();
                    navigate("/", { replace: true });
                  }}
                >
                  Restart
                </button>
                <button
                  className="btn btn-success text-white me-2"
                  onClick={downloadSignedPDF}
                  disabled={!isAllFieldsSigned()}
                >
                  Download Signed Contract
                </button>
                <button
                  className="btn btn-outline-primary"
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Signature Types Legend */}
        <div className="row mb-3">
          <div className="col-12">
            <div className="alert alert-info">
              <strong>Required Signatures:</strong>
              {getUniqueSignatureTypes().map((type) => (
                <span key={type} className="badge bg-primary me-2 ms-2">
                  {type.replace("-", " ").toUpperCase()}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Page Navigation */}
        <div className="row mb-3">
          <div className="col-12">
            <div className="d-flex justify-content-center align-items-center gap-3">
              {/* Page Navigation */}
              {numPages > 1 && (
                <div className="d-flex align-items-center">
                  <button
                    className="btn btn-outline-primary me-2"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage <= 1}
                  >
                    Previous
                  </button>
                  <span className="mx-3">
                    Page {currentPage} of {numPages}
                  </span>
                  <button
                    className="btn btn-outline-primary ms-2"
                    onClick={() =>
                      setCurrentPage(Math.min(numPages, currentPage + 1))
                    }
                    disabled={currentPage >= numPages}
                  >
                    Next
                  </button>
                </div>
              )}

              {/* Next Signature Navigation */}
              {getNextUnsignedField() && (
                <button
                  className="btn btn-outline-info"
                  onClick={navigateToNextSignature}
                  title="Go to next unsigned field"
                >
                  Next Signature →
                </button>
              )}

              {/* Zoom Controls */}
              <div className="d-flex align-items-center">
                <button
                  className="btn btn-sm btn-outline-secondary me-2"
                  onClick={() => setPageScale(Math.max(0.5, pageScale - 0.1))}
                >
                  Zoom Out
                </button>
                <span className="align-self-center mx-2">
                  {Math.round(pageScale * 100)}%
                </span>
                <button
                  className="btn btn-sm btn-outline-secondary ms-2"
                  onClick={() => setPageScale(Math.min(2.0, pageScale + 0.1))}
                >
                  Zoom In
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* PDF Viewer */}
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-center">
              <div
                className="pdf-container position-relative"
                style={{ minHeight: "800px" }}
              >
                <Document
                  file={`data:application/pdf;base64,${contractData.pdf}`}
                  onLoadSuccess={onDocumentLoadSuccess}
                  loading={<div>Loading PDF...</div>}
                  error={<div>Error loading PDF</div>}
                >
                  <Page
                    pageNumber={currentPage}
                    onLoadSuccess={onPageLoadSuccess}
                    scale={pageScale}
                    loading={<div>Loading page...</div>}
                  />

                  {/* Signature Field Overlays */}
                  {getFieldsForPage(currentPage).map((field, index) => {
                    const position = calculateFieldPosition(field);
                    const hasContent = hasFieldContent(field);
                    const isDateField = field.type.includes("date");
                    const dateValue = isDateField ? getFieldDate(field) : null;

                    return (
                      <div
                        key={`${field.type}-${index}`}
                        className={`signature-field ${
                          hasContent ? "signed" : "unsigned"
                        }`}
                        style={{
                          position: "absolute",
                          left: position.left,
                          top: position.top,
                          width: position.width,
                          height: position.height,
                          border: hasContent
                            ? "2px solid green"
                            : "2px dashed red",
                          backgroundColor: hasContent
                            ? "rgba(0, 255, 0, 0.1)"
                            : "rgba(255, 0, 0, 0.1)",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "12px",
                          color: hasContent ? "green" : "red",
                        }}
                        onClick={() => {
                          if (isDateField) {
                            openDateModal(field);
                          } else {
                            openSignatureModal(field);
                          }
                        }}
                        title={
                          isDateField
                            ? "Click to set date"
                            : `Click to ${
                                field.type.includes("initial")
                                  ? "initial"
                                  : "sign"
                              }`
                        }
                      >
                        {isDateField ? (
                          dateValue ? (
                            <span
                              style={{ fontSize: "10px", fontWeight: "bold" }}
                            >
                              {(() => {
                                const [year, month, day] = dateValue.split('-');
                                return new Date(year, month - 1, day).toLocaleDateString();
                              })()}
                            </span>
                          ) : (
                            <span>DATE</span>
                          )
                        ) : hasContent ? (
                          <img
                            src={hasContent}
                            alt="Signature"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                            }}
                          />
                        ) : (
                          <span>
                            {field.type.includes("initial")
                              ? "INITIAL"
                              : "SIGN"}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </Document>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Signature Modal */}
      {showSignatureModal && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {isInitialMode ? "Add Your Initials" : "Add Your Signature"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={clearSignatureModal}
                ></button>
              </div>
              <div className="modal-body">
                {/* Signature Mode Toggle */}
                <div className="mb-3">
                  <div className="btn-group w-100" role="group">
                    <button
                      type="button"
                      className={`btn ${
                        signatureMode === "draw"
                          ? "btn-primary"
                          : "btn-outline-primary"
                      }`}
                      onClick={() => setSignatureMode("draw")}
                    >
                      Draw {isInitialMode ? "Initials" : "Signature"}
                    </button>
                    <button
                      type="button"
                      className={`btn ${
                        signatureMode === "type"
                          ? "btn-primary"
                          : "btn-outline-primary"
                      }`}
                      onClick={() => setSignatureMode("type")}
                    >
                      Type {isInitialMode ? "Initials" : "Signature"}
                    </button>
                  </div>
                </div>

                {signatureMode === "draw" ? (
                  <div className="signature-pad-container">
                    <SignatureCanvas
                      ref={signatureCanvasRef}
                      canvasProps={{
                        width: 600,
                        height: isInitialMode ? 120 : 180,
                        className: "signature-canvas border",
                        style: {
                          width: "100%",
                          height: "auto",
                          maxWidth: "100%",
                        },
                      }}
                      backgroundColor="white"
                    />
                    <small className="text-muted">
                      {isInitialMode
                        ? "Draw your initials above"
                        : "Draw your signature above"}
                    </small>
                  </div>
                ) : (
                  <div className="typed-signature-container">
                    <div className="mb-3">
                      <label className="form-label">Font Style:</label>
                      <select
                        className="form-select"
                        value={selectedFont}
                        onChange={(e) => setSelectedFont(e.target.value)}
                      >
                        <option value="Brush Script MT">Brush Script MT</option>
                        <option value="Lucida Handwriting">
                          Lucida Handwriting
                        </option>
                        <option value="Monotype Corsiva">
                          Monotype Corsiva
                        </option>
                        <option value="Segoe Script">Segoe Script</option>
                        <option value="Dancing Script">Dancing Script</option>
                        <option value="Pacifico">Pacifico</option>
                        <option value="Great Vibes">Great Vibes</option>
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        {isInitialMode
                          ? "Enter your initials:"
                          : "Enter your full name:"}
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={typedSignature}
                        onChange={(e) => setTypedSignature(e.target.value)}
                        placeholder={isInitialMode ? "J.D." : "John Doe"}
                        style={{
                          fontFamily: `"${selectedFont}", cursive`,
                          fontSize: "24px",
                        }}
                      />
                    </div>
                    {typedSignature && (
                      <div className="signature-preview border p-3 text-center bg-light">
                        <div
                          style={{
                            fontFamily: `"${selectedFont}", cursive`,
                            fontSize: isInitialMode ? "36px" : "48px",
                            color: "black",
                          }}
                        >
                          {typedSignature}
                        </div>
                        <small className="text-muted">Preview</small>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                {signatureMode === "draw" && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => signatureCanvasRef.current?.clear()}
                  >
                    Clear
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={clearSignatureModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={saveSignature}
                  disabled={signatureMode === "type" && !typedSignature.trim()}
                >
                  Save {isInitialMode ? "Initials" : "Signature"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Date Modal */}
      {showDateModal && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Set Date</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={clearDateModal}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Select Date:</label>
                  <input
                    type="date"
                    className="form-control"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                  />
                </div>
                <div className="alert alert-info mb-0">
                  <small>
                    <strong>Preview:</strong>{" "}
                    {selectedDate
                      ? (() => {
                          const [year, month, day] = selectedDate.split('-');
                          return new Date(year, month - 1, day).toLocaleDateString();
                        })()
                      : "No date selected"}
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    const today = new Date().toISOString().split("T")[0];
                    setSelectedDate(today);
                  }}
                >
                  Use Today
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={clearDateModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={saveDate}
                  disabled={!selectedDate}
                >
                  Save Date
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContractSigning;
